import apiClient from '../utils/api';

export interface VendorLocaleEntry {
  companyName?: string;
  companyDescription?: string;
  servicePolicy?: string;
}

export interface VendorLocale {
  en?: VendorLocaleEntry;
  ar?: VendorLocaleEntry;
}

export interface UpdateCompanyProfileData {
  vendorId: string;
  // Step 2 fields
  companyName?: string;
  companyDescription?: string;
  companyWebsite?: string;
  // Contact info fields
  phone?: string;
  countryCode?: string;
  email?: string;
  contactName?: string;
  // Step 4 fields - Legal information
  registeredName?: string;
  tradeLicenseNumber?: string;
  commercialRegistrationNo?: string;
  registeredAddress?: string;
  // Service policy fields
  acceptOrdersDeadline?: number;
  acceptChangesDeadline?: number;
  servicePolicy?: string;
  // Locale (optional) for localized company info & service policy
  locale?: VendorLocale;
  // Which step to move to
  nextStep?: number;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
}

export interface VendorCategoryTree {
  mainCategory: string;
  subCategories: string[];
  types: string[];
  items: string[];
}

export interface VendorProfile {
  vendorId: string;
  email: string;
  contactName: string;
  phone: string;
  countryCode: string;
  businessType: string;
  companyName?: string;
  companyDescription?: string;
  companyWebsite?: string;
  profileImageUrl?: string;
  registeredName?: string;
  tradeLicenseNumber?: string;
  commercialRegistrationNo?: string;
  registeredAddress?: string;
  servicePolicy?: string;
  acceptOrdersDeadline?: number;
  acceptChangesDeadline?: number;
  shippingFeeInsideDoha?: number;
  shippingFeeOutsideDoha?: number;
  categoryTree?: VendorCategoryTree;
  locale?: VendorLocale;
}

export interface GetVendorProfileResponse {
  success: boolean;
  message?: string;
  error?: string;
  vendor?: VendorProfile;
}

export interface VendorEditableData {
  email?: string; // Contact email from application
  contactName?: string; // Contact person name from application
  phone?: string; // Contact phone from application
  countryCode?: string; // Country code from application
  companyName?: string;
  companyDescription?: string;
  companyWebsite?: string;
  profileImageUrl?: string; // S3 URL for company logo/profile image
  registeredName?: string;
  tradeLicenseNumber?: string;
  commercialRegistrationNo?: string;
  registeredAddress?: string;
  acceptOrdersDeadline?: number;
  acceptChangesDeadline?: number;
  servicePolicy?: string;
  shippingFeeInsideDoha?: number;
  shippingFeeOutsideDoha?: number;
  categoryTree?: VendorCategoryTree;
  occasionsIds?: string[];
  locale?: VendorLocale;
}

export const vendorProfileService = {
  /**
   * Get complete vendor profile
   */
  getVendorProfile: async (vendorId: string): Promise<GetVendorProfileResponse> => {
    const response = await apiClient.get<GetVendorProfileResponse>(
      `/vendors/${vendorId}/profile`
    );
    return response.data;
  },

  /**
   * Update vendor company profile (Step 2 of onboarding)
   */
  updateCompanyProfile: async (
    data: UpdateCompanyProfileData
  ): Promise<UpdateProfileResponse> => {
    const response = await apiClient.post<UpdateProfileResponse>(
      `/vendors/${data.vendorId}/update-profile`,
      data
    );
    return response.data;
  },

  editProfile: async (
    vendorId: string,
    data: VendorEditableData,
  ): Promise<GetVendorProfileResponse> => {
    const response = await apiClient.post<{ success: boolean, data: VendorProfile, message: string }>(
      `vendors/${vendorId}/edit-profile`,
      data
    );

    const responseData = response.data;

    return {
      success: responseData.success,
      message: responseData.message,
      vendor: responseData.data,
    }
  },
};

